/**
 * The shape of a briefing, so it can be read rather than only received.
 *
 * Leo, 2026-09-16: *"briefings are not quite friendly to read, theyre just a block of
 * text, we need to make sure they are properly displayed, with paragraphs and
 * semantically correct structure so theyre easy to read, mine for example list a bunch of
 * points in a single line, that makes it really hard to read and would be much better as a
 * bulleted list."*
 *
 * **That is two faults and this file exists because fixing either one alone looks like a
 * fix and is not.** The chat rendered every message with `white-space: pre-wrap`, so a
 * briefing was one grey block whatever was in it — but the deeper half is that the
 * generation prompt said, in as many words, *"No headings, no bullet lists, no markdown"*,
 * so the model wrote a sentence containing six comma-separated points. No renderer in
 * existence turns that sentence into a list. The structure has to be decided where the
 * briefing is written, which means it has to be something the model is *asked* for and
 * something the wire can carry.
 *
 * ## What is carried, and why it is text
 *
 * The model returns **blocks** — the schema in `packages/ingest/src/items.ts` gives it
 * nothing else to return — and `projectBriefing` turns those into one string with a
 * deliberately tiny set of markers: a blank line between blocks, `- ` in front of a bullet,
 * `## ` in front of a heading. That string is what is stored on the item, sent as the
 * message, written to the log and cut down for a push preview. `parseBriefing` reads it
 * back into blocks for anything that can render them.
 *
 * **The stored string is the plain-text projection, by construction, and that is the whole
 * reason the format is text.** A briefing reaches a learner through the chat, through a
 * push preview (`previewOf` in `packages/messaging/src/push.ts`, which collapses it to one
 * line) and historically as plain text in a message body; only the first of those can
 * render structure. A second DynamoDB attribute holding the blocks would have to be
 * threaded through `sweep.ts`, `transcript.ts`, the push payload and the daily summary, it
 * would be absent on all 125 briefings already live, and every reader would still need the
 * text path as a fallback — so the structured field would buy nothing and cost four
 * producers. One field, and the markers read as themselves anywhere that cannot do better.
 *
 * ## What was rejected
 *
 * **A Markdown library.** `marked` and `markdown-it` both pass raw HTML through by default
 * and both are used with `dangerouslySetInnerHTML`, and a briefing is model output written
 * from documents the *learner* supplied — a PDF is an injection surface, and this is the
 * one place its text reaches a page. Nothing here produces HTML: `parseBriefing` returns
 * strings that React puts in text nodes, so a briefing containing `<script>` renders as
 * the characters `<script>` and there is no code path where it does anything else. The
 * subset is three constructs because three is what a 250–300 word briefing needs; bold,
 * links, tables and code fences are deliberately absent and pass through as the literal
 * characters the model typed, which is ugly and never unsafe.
 *
 * **Prompting harder with no format.** That was the status quo. The instruction said plain
 * prose and the model complied perfectly; the complaint is about what plain prose does to a
 * list of six things.
 *
 * ## Legacy briefings
 *
 * Every briefing written before this file is plain prose, usually with blank lines between
 * paragraphs, and it parses into paragraph blocks and renders better than it did. One
 * written with `-` bullets by a model that ignored the old instruction now renders as a
 * list. Nothing blanks out: `parseBriefing` never returns an empty list for a non-empty
 * string, because a briefing that failed to parse must still be the thing the learner
 * reads.
 */
/**
 * The two markers, exported because the generation prompt names them to the model.
 *
 * A prompt that described the markers in its own words would be a second definition of
 * this format, and the failure would be silent: the model writes `* ` and `parseBriefing`
 * reads a paragraph beginning with an asterisk.
 */
export const BRIEFING_BULLET = '- ';
export const BRIEFING_HEADING = '## ';
/** A heading line, at any of Markdown's six depths. Everything deeper is not a heading. */
const HEADING_LINE = /^#{1,6}[ \t]+(.*)$/;
/**
 * A bullet line.
 *
 * `-`, `*` and `•` and no numbers, and the exclusion of numbers is load-bearing rather
 * than an omission. A multiple-choice question arrives as `1. Taiyuan` / `2. Lieque` with
 * *"Reply with the number"* under it (`questionText` in `packages/messaging/src/choice.ts`),
 * and turning those into an unordered list renumbers the learner's own answer — they read
 * "the third one", type 3, and are marked wrong. The three characters here cannot appear
 * at the head of a line for any other reason; a digit can.
 *
 * The space is required. `*emphasis*` at the start of a paragraph is not a list.
 */
const BULLET_LINE = /^[-*•][ \t]+(.*)$/;
/**
 * One block's text, flattened to a single line with no marker of its own.
 *
 * Two things, both of which keep `parseBriefing(projectBriefing(blocks))` equal to
 * `blocks` — a round trip that has to hold, because the projection is the only copy
 * anything downstream ever sees. A newline inside a block would re-parse as a block
 * boundary, so a model that writes a paragraph with a line break in it must not silently
 * gain a paragraph. And a model that writes its own `- ` in front of a bullet's text must
 * not produce `- - the point`, which is what happens if the marker is prepended blindly.
 */
function oneLine(value) {
    if (typeof value !== 'string')
        return '';
    const flat = value.replace(/\s+/g, ' ').trim();
    const heading = HEADING_LINE.exec(flat);
    if (heading)
        return (heading[1] ?? '').trim();
    const bullet = BULLET_LINE.exec(flat);
    if (bullet)
        return (bullet[1] ?? '').trim();
    return flat;
}
/**
 * The blocks as the one string everything downstream stores, sends and shows.
 *
 * Empty blocks are dropped rather than emitted, because a blank bullet renders as a
 * bullet with nothing beside it and reads as the briefing having lost a line.
 */
export function projectBriefing(blocks) {
    const rendered = [];
    for (const block of blocks) {
        if (block.kind === 'bullets') {
            const items = block.items.map(oneLine).filter((item) => item !== '');
            if (items.length > 0)
                rendered.push(items.map((item) => `${BRIEFING_BULLET}${item}`).join('\n'));
            continue;
        }
        const text = oneLine(block.text);
        if (text === '')
            continue;
        rendered.push(block.kind === 'heading' ? `${BRIEFING_HEADING}${text}` : text);
    }
    return rendered.join('\n\n');
}
/**
 * The stored string, read back as blocks.
 *
 * Total on every input, which is the property that matters: this runs on rows nobody
 * designed for it — 125 live briefings, every question, every correction, the daily
 * summary and whatever a learner typed — and a chat that throws on one of them shows no
 * conversation at all.
 *
 * **A non-empty string never parses to nothing.** Anything that produced no block at all
 * comes back as one paragraph holding the text unchanged, so the worst case is exactly the
 * behaviour this replaces rather than an empty bubble.
 */
export function parseBriefing(text) {
    const blocks = [];
    let paragraph = [];
    let bullets = [];
    const closeParagraph = () => {
        if (paragraph.length > 0)
            blocks.push({ kind: 'paragraph', text: paragraph.join('\n') });
        paragraph = [];
    };
    const closeBullets = () => {
        if (bullets.length > 0)
            blocks.push({ kind: 'bullets', items: bullets });
        bullets = [];
    };
    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (trimmed === '') {
            closeParagraph();
            closeBullets();
            continue;
        }
        const heading = HEADING_LINE.exec(trimmed);
        if (heading) {
            closeParagraph();
            closeBullets();
            const content = (heading[1] ?? '').trim();
            // A line of nothing but hashes is not a heading with an empty name, it is a rule
            // somebody typed. Kept as the paragraph text it looks like rather than dropped.
            if (content === '')
                paragraph.push(trimmed);
            else
                blocks.push({ kind: 'heading', text: content });
            continue;
        }
        const bullet = BULLET_LINE.exec(trimmed);
        if (bullet) {
            closeParagraph();
            const content = (bullet[1] ?? '').trim();
            if (content === '')
                paragraph.push(trimmed);
            else
                bullets.push(content);
            continue;
        }
        /*
         * A paragraph line closes a run of bullets, so prose under a list is prose and not a
         * fourth bullet with no marker. The line keeps its own place in the paragraph — the
         * lines are joined with `\n` and not with a space — because a question's options are
         * one per line and joining them would run them together.
         */
        closeBullets();
        paragraph.push(trimmed);
    }
    closeParagraph();
    closeBullets();
    if (blocks.length === 0 && text !== '')
        return [{ kind: 'paragraph', text }];
    return blocks;
}
//# sourceMappingURL=briefing.js.map